import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Chip,
  Skeleton,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  RestoreFromTrash as RestoreIcon,
  DeleteForever as PermanentDeleteIcon,
  DeleteSweep as EmptyTrashIcon,
} from "@mui/icons-material";
import * as imagesApi from "@api/images.api";
import { useNotification } from "@context/NotificationContext";
import { ConfirmDialog } from "@components/ConfirmDialog";
import type { Image } from "@/types";

const RETENTION_DAYS = 30;

function daysRemaining(deletedAt?: string): number {
  if (!deletedAt) return RETENTION_DAYS;
  const deleted = new Date(deletedAt).getTime();
  const now = Date.now();
  const elapsed = Math.floor((now - deleted) / (1000 * 60 * 60 * 24));
  return Math.max(0, RETENTION_DAYS - elapsed);
}

export default function Trash() {
  const { t } = useTranslation();
  const { notify } = useNotification();

  const [items, setItems] = useState<Image[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmRestore, setConfirmRestore] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [confirmEmpty, setConfirmEmpty] = useState(false);

  const loadTrash = useCallback(async () => {
    setLoading(true);
    try {
      const data = await imagesApi.getTrash();
      setItems(data);
    } catch {
      notify(t("trash.loadError", "Failed to load trash"), "error");
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    loadTrash();
  }, [loadTrash]);

  const handleRestore = useCallback(
    async (id: string) => {
      try {
        await imagesApi.restore(id);
        notify(t("trash.restored", "Image restored"), "success");
        setItems((prev) => prev.filter((i) => i.id !== id));
      } catch {
        notify(t("trash.restoreError", "Restore failed"), "error");
      }
      setConfirmRestore(null);
    },
    [notify, t],
  );

  const handlePermanentDelete = useCallback(
    async (id: string) => {
      try {
        await imagesApi.permanentDelete(id);
        notify(
          t("trash.permanentlyDeleted", "Image permanently deleted"),
          "success",
        );
        setItems((prev) => prev.filter((i) => i.id !== id));
      } catch {
        notify(t("trash.deleteError", "Delete failed"), "error");
      }
      setConfirmDelete(null);
    },
    [notify, t],
  );

  const handleEmptyTrash = useCallback(async () => {
    try {
      await Promise.all(items.map((i) => imagesApi.permanentDelete(i.id)));
      notify(t("trash.emptied", "Trash emptied"), "success");
      setItems([]);
    } catch {
      notify(t("trash.emptyError", "Failed to empty trash"), "error");
    }
    setConfirmEmpty(false);
  }, [items, notify, t]);

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Skeleton variant="text" width={200} height={40} />
        {[...Array(3)].map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={60}
            sx={{ mt: 1, borderRadius: 1 }}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
          }}
        >
          {t("trash.title", "Trash")}
        </Typography>

        {items.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<EmptyTrashIcon />}
            onClick={() => setConfirmEmpty(true)}
          >
            {t("trash.emptyTrash", "Empty Trash")}
          </Button>
        )}
      </Box>
      {items.length === 0 ? (
        <Alert severity="info">{t("trash.empty", "Trash is empty.")}</Alert>
      ) : (
        <Card>
          <List>
            {items.map((img) => {
              const remaining = daysRemaining(img.deletedAt);
              return (
                <ListItem key={img.id} divider>
                  <ListItemText
                    primary={img.name}
                    secondary={`${(img.size / 1024).toFixed(1)} KB`}
                  />
                  <Chip
                    label={t("trash.daysRemaining", "{{days}}d left", {
                      days: remaining,
                    })}
                    size="small"
                    color={remaining <= 5 ? "error" : "default"}
                    sx={{ mr: 1 }}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title={t("trash.restore", "Restore")}>
                      <IconButton
                        onClick={() => setConfirmRestore(img.id)}
                        color="primary"
                      >
                        <RestoreIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={t("trash.permanentDelete", "Delete permanently")}
                    >
                      <IconButton
                        onClick={() => setConfirmDelete(img.id)}
                        color="error"
                      >
                        <PermanentDeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        </Card>
      )}
      <ConfirmDialog
        open={!!confirmRestore}
        title={t("trash.confirmRestoreTitle", "Restore Image")}
        message={t(
          "trash.confirmRestoreMsg",
          "This image will be moved back to your gallery.",
        )}
        onConfirm={() => confirmRestore && handleRestore(confirmRestore)}
        onCancel={() => setConfirmRestore(null)}
        confirmLabel={t("trash.restore", "Restore")}
        confirmColor="primary"
      />
      <ConfirmDialog
        open={!!confirmDelete}
        title={t("trash.confirmDeleteTitle", "Permanently Delete")}
        message={t(
          "trash.confirmDeleteMsg",
          "This action cannot be undone. The image and its data will be permanently removed.",
        )}
        onConfirm={() => confirmDelete && handlePermanentDelete(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
        confirmLabel={t("trash.permanentDelete", "Delete permanently")}
      />
      <ConfirmDialog
        open={confirmEmpty}
        title={t("trash.confirmEmptyTitle", "Empty Trash")}
        message={t(
          "trash.confirmEmptyMsg",
          "All {{count}} images will be permanently deleted. This cannot be undone.",
          { count: items.length },
        )}
        onConfirm={handleEmptyTrash}
        onCancel={() => setConfirmEmpty(false)}
        confirmLabel={t("trash.emptyTrash", "Empty Trash")}
      />
    </Box>
  );
}
